import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

import { get, post } from '../api-config';
import { REACT_APP_API_URL } from '../../app/app.config';

import { ICreateTransactionRequest, ITransaction, IUpdateTransactionContactsRequest } from './types';
import toast from 'react-hot-toast';
import { Z_INDEX } from '../../constants/zIndex';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export const getGuestById = createAsyncThunk(
    'guest/fetchById',
    async (id: string, { signal, rejectWithValue }) => {
        const source = axios.CancelToken.source();
        signal.addEventListener('abort', () => {
            source.cancel();
        });

        try {
            return await get<ITransaction, unknown>(
                `/commercial/transactions/${id}`,
                null,
                source.token
            );
        } catch (error) {
            const data = (error as { response: { data: unknown } }).response.data;
            return rejectWithValue(data);
        }
    }
);

const ACCESS_TOKEN_NAME = 'token';

export const transactionsApi = createApi({
    reducerPath: 'transactionsApi',
    baseQuery: fetchBaseQuery({
        baseUrl: REACT_APP_API_URL,
        prepareHeaders: headers => {
            const token = localStorage.getItem(ACCESS_TOKEN_NAME);
            if (token) {
                headers.set('authorization', `Bearer ${token}`);
            }
            headers.set('Accept', 'application/json');
            headers.set('Accept-Language', 'en');
            headers.set('Content-Type', 'application/json');
            return headers;
        },
    }),
    tagTypes: ['Transaction'],
    endpoints: builder => ({
        createTransaction: builder.mutation<ITransaction, ICreateTransactionRequest>({
            query: createTransactionData => ({
                url: '/commercial/transactions',
                method: 'POST',
                body: createTransactionData,
            }),
            invalidatesTags: ['Transaction'],
            async onQueryStarted(arg, { queryFulfilled }) {
                try {
                    await queryFulfilled;
                } catch (error: any) {
                    const data = error?.response?.data;
                    if (data?.message) {
                        toast.error(data.message, {
                            style: {
                                zIndex: Z_INDEX.TOAST,
                            },
                        });
                    }
                }
            },
        }),
        getTransactionById: builder.query<ITransaction, string>({
            query: id => `/commercial/transactions/${id}`,
            providesTags: (result, error, id) => [{ type: 'Transaction', id }],
        }),
        stopTransaction: builder.mutation<ITransaction, string>({
            query: id => ({
                url: `/commercial/transactions/${id}/stop`,
                method: 'POST',
            }),
            invalidatesTags: (result, error, id) => [{ type: 'Transaction', id }],
        }),
        updateTransactionContacts: builder.mutation<ITransaction, IUpdateTransactionContactsRequest>({
            query: updateTransactionContactsData => ({
                url: `/commercial/transactions/${updateTransactionContactsData.transactionId}/contact-details`,
                method: 'PUT',
                body: updateTransactionContactsData,
            }),
            invalidatesTags: ['Transaction'],
            async onQueryStarted(arg, { queryFulfilled }) {
                try {
                    await queryFulfilled;
                } catch (error: any) {
                    const data = error?.response?.data;
                    if (data?.message) {
                        toast.error(data.message, {
                            style: {
                                zIndex: Z_INDEX.TOAST,
                            },
                        });
                    }
                }
            },
        }),
    }),
});

export const {
    useCreateTransactionMutation,
    useGetTransactionByIdQuery,
    useStopTransactionMutation,
    useUpdateTransactionContactsMutation,
} = transactionsApi;

export const stopCharge = (id: string) => {
    return post<ITransaction, unknown>(`/commercial/transactions/${id}/stop`);
};

export const getInvoiceLink = (id: string) => {
    return `${REACT_APP_API_URL}/${id}/invoice`;
};

export const registerPushDevice = (commercialTransactionId: string, fid: string) =>
    post<void, { fid: string }>(`/commercial/transactions/${commercialTransactionId}/devices`, { fid });
